import { Facebook, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { absoluteApi } from "../lib/api";

/**
 * Per-post share row. Sits above and/or below post body on PostDetail.
 * Includes Facebook + Email + Copy-link (Instagram doesn't support web share intents).
 *
 * Facebook's crawler doesn't run JS so it can't see SPA-injected meta tags.
 * For the FB share dialog we point at /api/share/{slug} which returns a
 * server-rendered HTML page with full Open Graph tags for the post and
 * meta-refresh-redirects humans to the SPA route. We use absoluteApi()
 * because the URL we hand to Facebook must be reachable from FB's own
 * crawler — relative paths would break.
 */
export default function ShareButtons({ title, slug, url, className = "" }) {
  const fbTargetUrl = slug ? `${absoluteApi()}/share/${slug}` : url;
  const encodedFbUrl = encodeURIComponent(fbTargetUrl);
  const encodedHumanUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedFbUrl}`;
  const emailHref = `mailto:?subject=${encodedTitle}&body=Thought you'd enjoy this from The Tea List:%0A%0A${encodedHumanUrl}`;

  const copyLink = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const Item = ({ href, onClick, label, Icon, testid }) => (
    <a
      href={href}
      onClick={onClick}
      target={onClick ? undefined : "_blank"}
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="inline-flex items-center justify-center w-9 h-9 border text-[#404e48] hover:text-[#1e4a35] hover:border-[#1e4a35] transition-colors"
      style={{ borderColor: "var(--border)" }}
      data-testid={testid}
    >
      <Icon size={14} strokeWidth={1.7} />
    </a>
  );

  return (
    <div
      className={`flex items-center gap-3 ${className}`}
      data-testid="share-buttons"
    >
      <span className="font-mono text-[10px] tracking-widest uppercase text-[#66726d] mr-1">
        Share
      </span>
      <Item href={facebookHref} label="Share on Facebook" Icon={Facebook} testid="share-facebook" />
      <Item href={emailHref} label="Share by email" Icon={Mail} testid="share-email" />
      <Item href="#" onClick={copyLink} label="Copy link" Icon={Link2} testid="share-copy-link" />
    </div>
  );
}
